import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Star, Award, Tag, ChevronRight, Phone, Mail, Globe as GlobeIcon, 
  Clock, ArrowLeft, ExternalLink, Share2, Heart, MessageSquare, CheckCircle,
  Utensils, Home, Car, Heart as HeartIcon, Sparkles, Briefcase, Building,
  Dumbbell, GraduationCap, PawPrint, Music, ShoppingBag, DollarSign,
  Laptop, Hammer, Scale, Stethoscope, Globe, Coffee, Plane, Loader2, Send,
  MessageCircle, User, Calendar, Check, FileText, Video, Image, Ticket, Download, Play, Eye,
  Megaphone, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { BookmarkButton, ReviewReplySection, MemberChatModal } from '../components/social';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Content type icons mapping
const CONTENT_TYPE_ICONS = {
  blog_article: FileText,
  community_article: FileText,
  discussion: MessageSquare,
  video: Video,
  photo_album: Image,
  coupon: Ticket,
  event: Calendar,
  job_listing: Briefcase,
  product: ShoppingBag,
  digital_product: Download,
  property: Building,
  classified: Tag,
  soundcloud_post: Music,
};

// Directory Logo
const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

const CATEGORY_ICONS = {
  'restaurants': Utensils, 'home-services': Home, 'automotive': Car,
  'health-wellness': HeartIcon, 'beauty': Sparkles, 'professional-services': Briefcase,
  'real-estate': Building, 'fitness': Dumbbell, 'education': GraduationCap,
  'pet-services': PawPrint, 'entertainment': Music, 'retail': ShoppingBag,
  'financial': DollarSign, 'technology': Laptop, 'construction': Hammer,
  'legal': Scale, 'medical': Stethoscope, 'ecommerce': Globe,
  'food-beverage': Coffee, 'travel-hospitality': Plane,
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [
  'Morning (8am-12pm)',
  'Afternoon (12pm-5pm)',
  'Evening (5pm-8pm)',
  'Any time'
];

function StarRating({ rating, size = 'md', interactive = false, onChange }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          onClick={() => interactive && onChange && onChange(star)}
          className={`${sizeClass} ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
        />
      ))}
    </div>
  );
}

// Contact Form Component
function ContactForm({ businessId, businessName, onSuccess }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    comment: '',
    best_day: '',
    best_time: '',
    opt_in: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/directory/businesses/${businessId}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          business_name: businessName
        })
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('Your message has been sent!');
        if (onSuccess) onSuccess();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
        <p className="text-gray-400 mb-4">
          Thank you for reaching out. {businessName} will contact you soon.
        </p>
        <Button
          onClick={() => {
            setSubmitted(false);
            setFormData({
              first_name: '',
              last_name: '',
              email: '',
              phone: '',
              comment: '',
              best_day: '',
              best_time: '',
              opt_in: false
            });
          }}
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10"
        >
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300 text-sm">First Name *</Label>
          <Input
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            placeholder="John"
            className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            required
          />
        </div>
        <div>
          <Label className="text-gray-300 text-sm">Last Name *</Label>
          <Input
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            placeholder="Smith"
            className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            required
          />
        </div>
      </div>

      <div>
        <Label className="text-gray-300 text-sm">Email *</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@example.com"
          className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
          required
        />
      </div>

      <div>
        <Label className="text-gray-300 text-sm">Phone (Optional)</Label>
        <Input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="(555) 123-4567"
          className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
        />
      </div>

      <div>
        <Label className="text-gray-300 text-sm">What do you need help with? *</Label>
        <Textarea
          value={formData.comment}
          onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
          placeholder="Please describe what you're looking for..."
          rows={4}
          className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300 text-sm">Best Day to Reach You</Label>
          <select
            value={formData.best_day}
            onChange={(e) => setFormData({ ...formData, best_day: e.target.value })}
            className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
          >
            <option value="">Select a day</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day} value={day}>{day}</option>
            ))}
            <option value="Any day">Any day</option>
          </select>
        </div>
        <div>
          <Label className="text-gray-300 text-sm">Best Time to Reach You</Label>
          <select
            value={formData.best_time}
            onChange={(e) => setFormData({ ...formData, best_time: e.target.value })}
            className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
          >
            <option value="">Select a time</option>
            {TIME_SLOTS.map((time) => (
              <option key={time} value={time}>{time}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Opt-in Checkbox */}
      <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
        <input
          type="checkbox"
          id="opt-in"
          checked={formData.opt_in}
          onChange={(e) => setFormData({ ...formData, opt_in: e.target.checked })}
          className="mt-1 w-4 h-4 rounded border-gray-600 bg-white/10 text-cyan-500 focus:ring-cyan-500"
        />
        <label htmlFor="opt-in" className="text-sm text-gray-400">
          I agree to receive marketing communications from {businessName}. You can unsubscribe at any time. 
          View our <a href="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</a>.
        </label>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send Message
          </>
        )}
      </Button>

      <p className="text-xs text-gray-500 text-center">
        By submitting this form, you agree to our Terms of Service and acknowledge our Privacy Policy.
      </p>
    </form>
  );
}

export default function BusinessDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [business, setBusiness] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [publishedContent, setPublishedContent] = useState([]);
  const [ownerPromotions, setOwnerPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, title: '', content: '', reviewer_name: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatRecipient, setChatRecipient] = useState(null);
  const [referrerInfo, setReferrerInfo] = useState(null);
  
  const token = localStorage.getItem('token');
  const refCode = searchParams.get('ref');
  
  let currentUserId = null;
  let isAdmin = false;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      currentUserId = payload.user_id;
      isAdmin = payload.role === 'admin';
    } catch (e) {
      // Token parse error - ignore
    }
  }
  
  // Check if current user is the business owner OR admin (for demo purposes)
  const isBusinessOwner = business?.claimed_by === currentUserId || isAdmin;

  useEffect(() => {
    loadData();
    // Track affiliate click if ref parameter present
    if (refCode) {
      trackAffiliateClick(refCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refCode]);

  const trackAffiliateClick = async (ref) => {
    try {
      // Track the click
      await fetch(`${API_URL}/api/affiliate/track-click/${ref}`, { method: 'POST' });
      
      // Fetch referrer info for the banner
      const infoRes = await fetch(`${API_URL}/api/affiliate/referrer-info/${ref}`);
      if (infoRes.ok) {
        const info = await infoRes.json();
        setReferrerInfo(info);
      }
    } catch (error) {
      console.error('Failed to track affiliate click:', error);
    }
  };

  const loadData = async () => {
    try {
      const [bizRes, reviewsRes, couponsRes, contentRes] = await Promise.all([
        fetch(`${API_URL}/api/directory/businesses/${id}`),
        fetch(`${API_URL}/api/directory/reviews/${id}?limit=20`),
        fetch(`${API_URL}/api/directory/coupons?business_id=${id}`),
        fetch(`${API_URL}/api/directory/content/business/${id}`)
      ]);
      
      if (!bizRes.ok) throw new Error('Business not found');
      
      const businessData = await bizRes.json();
      setBusiness(businessData);
      setReviews(await reviewsRes.json());
      setCoupons(await couponsRes.json());
      
      if (contentRes.ok) {
        const contentData = await contentRes.json();
        setPublishedContent(contentData.content || []);
      }
      
      // Load owner's promotions if business is claimed
      if (businessData.claimed_by) {
        try {
          const promoRes = await fetch(`${API_URL}/api/affiliate/user/${businessData.claimed_by}/promotions?limit=6`);
          if (promoRes.ok) {
            const promoData = await promoRes.json();
            setOwnerPromotions(promoData.promotions || []);
          }
        } catch (e) {
          console.error('Failed to load owner promotions:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load:', error);
      toast.error('Business not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newReview.reviewer_name || !newReview.content) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmittingReview(true);
    try {
      const res = await fetch(`${API_URL}/api/directory/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newReview, business_id: id })
      });
      
      if (res.ok) {
        toast.success('Review submitted!');
        setShowReviewForm(false);
        setNewReview({ rating: 5, title: '', content: '', reviewer_name: '' });
        loadData();
      }
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Handle contact form success - track as affiliate conversion
  const handleContactSuccess = async () => {
    if (refCode) {
      try {
        await fetch(`${API_URL}/api/affiliate/track-conversion/${refCode}?type=contact`, { method: 'POST' });
      } catch (error) {
        console.error('Failed to track conversion:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center text-white">
        <div className="text-center">
          <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Business Not Found</h2>
          <Link to="/directory"><Button className="bg-cyan-500 hover:bg-cyan-600 text-white">Back to Directory</Button></Link>
        </div>
      </div>
    );
  }

  const Icon = CATEGORY_ICONS[business.category] || Globe;

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0f1a]/90 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/directory" className="flex items-center gap-2 text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Link>
            <div className="h-4 w-px bg-white/20" />
            <Link to="/directory" className="flex items-center gap-2">
              <img src={DIRECTORY_LOGO} alt="Directory" className="h-8 w-auto" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {token && <BookmarkButton businessId={id} variant="header" />}
            {token && (
              <Button 
                variant="outline" 
                size="sm" 
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/directory/businesses/${id}/promote`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                      toast.success('Added to your affiliate promotions!');
                    } else {
                      const data = await res.json();
                      toast.error(data.detail || 'Failed to promote');
                    }
                  } catch (error) {
                    toast.error('Failed to add promotion');
                  }
                }}
                data-testid="promote-business-btn"
              >
                <Megaphone className="w-4 h-4 mr-1" /> Promote
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative">
        <div className="h-48 md:h-64 bg-gradient-to-r from-cyan-600 to-teal-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#0a0f1a] to-transparent" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 -mt-20 relative z-10">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-[#0d1525] border-4 border-[#0a0f1a] shadow-xl overflow-hidden flex items-center justify-center">
              {business.logo ? (
                <img src={business.logo} alt={business.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center">
                  <Icon className="w-16 h-16 text-cyan-400" />
                </div>
              )}
            </div>
            
            <div className="flex-1 pt-4">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-4xl font-bold text-white">{business.name}</h1>
                {business.is_ai_certified && (
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full border border-amber-500/30">
                    <Award className="w-4 h-4" />
                    <span className="text-sm font-medium">Best of Best</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4 text-gray-400 mb-4">
                <span className="capitalize">{business.subcategory}</span>
                <span>•</span>
                <span>{business.city}, {business.state}</span>
              </div>
              
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <StarRating rating={Math.round(business.rating)} />
                  <span className="font-semibold text-white">{business.rating}</span>
                  <span className="text-gray-500">({business.review_count} reviews)</span>
                </div>
                {business.price_range && (
                  <span className="text-green-400">{business.price_range}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-gray-300 text-lg leading-relaxed">{business.description}</p>
        </div>
        
        {/* About Us Section - Rich Text Content */}
        {business.about_us && (
          <div className="mb-8 bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              About Us
            </h2>
            <div 
              className="prose prose-invert prose-cyan max-w-none text-gray-300"
              dangerouslySetInnerHTML={{ __html: business.about_us }}
            />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Form Section - NEW */}
            <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-2xl p-6 border border-cyan-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-white">Contact {business.name}</h2>
                  <p className="text-sm text-gray-400">Get in touch - no account required</p>
                </div>
              </div>
              <ContactForm 
                businessId={id} 
                businessName={business.name}
                onSuccess={handleContactSuccess}
              />
            </div>

            {/* Help Promote This Company - Affiliate Section */}
            {business.affiliate_program?.is_active && (
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20" data-testid="help-promote-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Megaphone className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-white">Help Promote {business.name}</h2>
                    <p className="text-sm text-gray-400">Join our affiliate program and earn rewards</p>
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-white/5 rounded-xl">
                    <p className="text-2xl font-bold text-purple-400">
                      {business.affiliate_program.commission_type === 'percentage' 
                        ? `${business.affiliate_program.commission_rate || 10}%`
                        : `$${business.affiliate_program.commission_amount || business.affiliate_program.commission_rate || 10}`}
                    </p>
                    <p className="text-xs text-gray-500">Per Conversion</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-xl">
                    <p className="text-2xl font-bold text-cyan-400">{business.affiliate_program.credit_reward || 10}</p>
                    <p className="text-xs text-gray-500">Publishing Credits</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-xl">
                    <p className="text-2xl font-bold text-green-400">{business.affiliate_program.cookie_duration || 30}</p>
                    <p className="text-xs text-gray-500">Day Cookie</p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/register?intent=affiliate" className="flex-1">
                    <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                      <Users className="w-4 h-4 mr-2" />
                      Become an Affiliate
                    </Button>
                  </Link>
                  <Link to="/affiliate" className="flex-1">
                    <Button variant="outline" className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Programs We Promote - Owner's Affiliate Programs */}
            {ownerPromotions.length > 0 && (
              <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10" data-testid="owner-promotions-section">
                <div className="flex items-center gap-2 mb-4">
                  <Share2 className="w-5 h-5 text-cyan-400" />
                  <h2 className="font-heading text-lg font-semibold text-white">Programs We Promote</h2>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  {business.name} is an affiliate partner with these businesses
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {ownerPromotions.map((promo) => (
                    <Link
                      key={promo.id || promo.affiliate_id}
                      to={`/directory/business/${promo.business_id}`}
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group"
                    >
                      {promo.business_logo ? (
                        <img 
                          src={promo.business_logo} 
                          alt={promo.business_name} 
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                          <Building className="w-5 h-5 text-cyan-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate group-hover:text-cyan-400 transition-colors">
                          {promo.business_name || promo.name || 'Partner Business'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {promo.business_category || 'Affiliate Partner'}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-cyan-400 transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Coupons */}
            {coupons.length > 0 && (
              <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="w-5 h-5 text-emerald-400" />
                  <h2 className="font-heading text-xl font-semibold text-white">Available Deals</h2>
                </div>
                <div className="space-y-3">
                  {coupons.map((coupon) => (
                    <div key={coupon.id} className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-white">{coupon.title}</h3>
                          <p className="text-sm text-gray-400">{coupon.description}</p>
                        </div>
                        {coupon.code && (
                          <code className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded text-emerald-400 font-mono">{coupon.code}</code>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Latest from Business - Published Content Section */}
            {publishedContent.length > 0 && (
              <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10" data-testid="latest-from-business">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <h2 className="font-heading text-xl font-semibold text-white">Latest from {business.name}</h2>
                  <span className="text-gray-400">({publishedContent.length})</span>
                </div>
                <div className="space-y-3">
                  {publishedContent.slice(0, 5).map((content) => {
                    const ContentIcon = CONTENT_TYPE_ICONS[content.content_type] || FileText;
                    return (
                      <Link 
                        key={content.id} 
                        to={`/directory/content/${content.id}`}
                        className="block p-4 bg-white/5 border border-white/10 rounded-xl hover:border-purple-500/30 hover:bg-white/10 transition-all group"
                        data-testid={`content-${content.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {content.images?.[0] ? (
                            <img 
                              src={content.images[0]} 
                              alt={content.title}
                              className="w-16 h-16 rounded-lg object-cover shrink-0"
                            />
                          ) : content.video_url ? (
                            <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
                              <Play className="w-6 h-6 text-purple-400" />
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
                              <ContentIcon className="w-6 h-6 text-purple-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400 capitalize">
                                {content.content_type.replace(/_/g, ' ')}
                              </span>
                              {content.is_featured && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">
                                  Featured
                                </span>
                              )}
                            </div>
                            <h3 className="font-medium text-white group-hover:text-purple-400 transition-colors truncate">
                              {content.title}
                            </h3>
                            <p className="text-sm text-gray-400 line-clamp-2">{content.description}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" /> {content.views || 0} views
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" /> {content.likes || 0}
                              </span>
                              <span>{new Date(content.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                {publishedContent.length > 5 && (
                  <Button 
                    variant="ghost" 
                    className="w-full mt-4 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                  >
                    View all {publishedContent.length} posts
                  </Button>
                )}
              </div>
            )}

            {/* Reviews - Dark Theme */}
            <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-white">Reviews</h2>
                  <span className="text-gray-400">({reviews.length})</span>
                </div>
                <Button onClick={() => setShowReviewForm(!showReviewForm)} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  Write a Review
                </Button>
              </div>

              {/* Review Form - Dark Theme */}
              {showReviewForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  onSubmit={handleSubmitReview}
                  className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl"
                >
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Your Rating</label>
                    <StarRating rating={newReview.rating} size="lg" interactive onChange={(r) => setNewReview({...newReview, rating: r})} />
                  </div>
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Your Name</label>
                    <Input value={newReview.reviewer_name} onChange={(e) => setNewReview({...newReview, reviewer_name: e.target.value})} placeholder="John D." className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                  </div>
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Title (optional)</label>
                    <Input value={newReview.title} onChange={(e) => setNewReview({...newReview, title: e.target.value})} placeholder="Great experience!" className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                  </div>
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Your Review</label>
                    <Textarea value={newReview.content} onChange={(e) => setNewReview({...newReview, content: e.target.value})} placeholder="Share your experience..." rows={4} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-white" disabled={submittingReview}>
                      {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Submit Review
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowReviewForm(false)} className="text-gray-400">Cancel</Button>
                  </div>
                </motion.form>
              )}

              {/* Reviews List - Dark Theme */}
              <div className="space-y-4">
                {reviews.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No reviews yet. Be the first to review!</p>
                ) : reviews.map((review) => (
                  <div key={review.id} className="p-4 bg-white/5 rounded-xl border border-white/5" data-testid={`review-${review.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-white">{review.reviewer_name}</p>
                        <StarRating rating={review.rating} size="sm" />
                      </div>
                      <span className="text-xs text-gray-500">{new Date(review.created_at).toLocaleDateString()}</span>
                    </div>
                    {review.title && <h4 className="font-medium text-white mb-1">{review.title}</h4>}
                    <p className="text-gray-400 text-sm">{review.content}</p>
                    
                    {/* Review Reply Section */}
                    <ReviewReplySection 
                      reviewId={review.id}
                      businessId={id}
                      canReply={isBusinessOwner}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Dark Theme */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">Contact Info</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white">{business.address}</p>
                    <p className="text-gray-400">{business.city}, {business.state} {business.zip_code}</p>
                  </div>
                </div>
                {business.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-500" />
                    <a href={`tel:${business.phone}`} className="text-cyan-400 hover:underline">{business.phone}</a>
                  </div>
                )}
                {business.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <a href={`mailto:${business.email}`} className="text-cyan-400 hover:underline">{business.email}</a>
                  </div>
                )}
                {business.website && (
                  <div className="flex items-center gap-3">
                    <GlobeIcon className="w-5 h-5 text-gray-500" />
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline flex items-center gap-1">
                      Visit Website <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Hours */}
            {business.hours && (
              <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <h3 className="font-heading text-lg font-semibold text-white">Business Hours</h3>
                </div>
                <div className="space-y-2 text-sm">
                  {Object.entries(business.hours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between">
                      <span className="text-gray-400 capitalize">{day}</span>
                      <span className="text-white">{hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {business.amenities?.length > 0 && (
              <div className="bg-[#0d1525] rounded-2xl p-6 border border-white/10">
                <h3 className="font-heading text-lg font-semibold text-white mb-4">Amenities</h3>
                <div className="flex flex-wrap gap-2">
                  {business.amenities.map((amenity, i) => (
                    <span key={i} className="flex items-center gap-1 px-3 py-1 bg-cyan-500/10 text-cyan-400 text-sm rounded-full border border-cyan-500/20">
                      <CheckCircle className="w-3 h-3" /> {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Message Business Owner - Only show if business is claimed */}
            {business.claimed_by && business.claimed_by !== currentUserId && token && (
              <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-2xl p-6 border border-cyan-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Message Owner</h3>
                    <p className="text-sm text-gray-400">Send a direct message</p>
                  </div>
                </div>
                <Button 
                  onClick={() => {
                    setChatRecipient({ id: business.claimed_by, name: business.name });
                    setShowChatModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Start Conversation
                </Button>
              </div>
            )}

            {/* Affiliate Referral Banner */}
            {refCode && referrerInfo?.found && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-4 border border-amber-500/30"
                data-testid="referrer-banner"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    {referrerInfo.referrer_business_logo ? (
                      <img 
                        src={referrerInfo.referrer_business_logo} 
                        alt={referrerInfo.referrer_name} 
                        className="w-12 h-12 rounded-full object-cover border-2 border-amber-500/50"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-amber-500/30 flex items-center justify-center">
                        <Users className="w-6 h-6 text-amber-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-amber-300 font-medium">
                        Recommended by {referrerInfo.referrer_name}
                      </p>
                      {referrerInfo.referrer_business_name && (
                        <p className="text-sm text-amber-400/70">
                          from {referrerInfo.referrer_business_name}
                        </p>
                      )}
                    </div>
                  </div>
                  {referrerInfo.referrer_business_id && (
                    <Link 
                      to={`/directory/business/${referrerInfo.referrer_business_id}`}
                      className="px-3 py-1.5 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg border border-amber-500/30 transition-colors"
                    >
                      View Their Business
                    </Link>
                  )}
                </div>
              </motion.div>
            )}
            {/* Simple fallback if referrer info not loaded */}
            {refCode && !referrerInfo?.found && referrerInfo !== null && (
              <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                <p className="text-sm text-amber-400">
                  <span className="font-semibold">Referred by affiliate:</span> {refCode}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      <MemberChatModal 
        isOpen={showChatModal}
        onClose={() => {
          setShowChatModal(false);
          setChatRecipient(null);
        }}
        initialRecipient={chatRecipient}
      />
    </div>
  );
}
